/**
 * Meta Pixel (Facebook Pixel) — client-side wrapper.
 *
 * Cubre todo el ciclo de vida client-side:
 *   - `loadMetaPixel()`  → inyecta fbevents.js y dispara init + primer PageView
 *   - `trackMetaEvent()` / `trackMetaCustomEvent()` → estándar y custom
 *   - `trackPageViewMetaPixel()` → PageView para SPA route changes
 *   - `trackPurchase()` / `trackInitiateCheckout()` / `trackLead()` / `trackViewContent()`
 *     helpers para eventos comunes del funnel
 *   - `generateMetaEventId()` → UUID para dedup con Conversions API
 *   - `getFbpFbcCookies()` → lee `_fbp` y `_fbc` para forward al CAPI server-side
 *
 * Carga lazy (post-interaction o idle) vía `src/lib/loadAnalytics.ts` para no
 * impactar FCP/LCP. Si el pixel ID está vacío, todo es noop.
 *
 * El 4° parámetro `{ eventID }` se usa para deduplicar con eventos enviados
 * desde server-side (api/lib/metaCapi.ts). El mismo ID en ambos lados =
 * Meta cuenta UNA conversión, no dos.
 */

import { BRAND_META_PIXEL_ID } from "@/lib/brandConfig";

const PIXEL_SCRIPT_SRC = "https://connect.facebook.net/en_US/fbevents.js";
const PIXEL_INITIALIZED_KEY = "__metaPixelInitialized";

/** True si fbevents.js ya está descargado y `fbq` es función. */
export function isMetaPixelLoaded(): boolean {
  return typeof window !== "undefined" && typeof window.fbq === "function";
}

/** True si ya corrió `fbq('init', ...)` en esta sesión de tab. */
function isPixelAlreadyInitialized(): boolean {
  if (typeof window === "undefined") return false;
  return Boolean((window as unknown as Record<string, unknown>)[PIXEL_INITIALIZED_KEY]);
}

function markPixelInitialized(): void {
  if (typeof window === "undefined") return;
  (window as unknown as Record<string, unknown>)[PIXEL_INITIALIZED_KEY] = true;
}

/** Inyecta el stub estándar de Meta Pixel (idéntico al snippet del dashboard). */
function injectFbqStub(): void {
  if (typeof window === "undefined") return;
  if (typeof window.fbq === "function") return;

  // Stub oficial (reproducido textualmente del snippet de Meta para que `fbq`
  // exista antes de que fbevents.js termine de cargar y cuele las llamadas).
  /* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-expressions, prefer-rest-params, prefer-spread */
  (function (f: any, b: any, e: string, v: string) {
    if (f.fbq) return;
    const n: any = (f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    });
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = "2.0";
    n.queue = [];
    const t = b.createElement(e);
    t.async = !0;
    t.src = v;
    const s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, "script", PIXEL_SCRIPT_SRC);
  /* eslint-enable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-expressions, prefer-rest-params, prefer-spread */
}

/**
 * Carga fbevents.js, ejecuta `fbq('init', pixelId, advancedMatching?)` y dispara
 * un PageView inicial. Idempotente: llamarlo varias veces no duplica el init.
 *
 * @param pixelId Override opcional; por default usa `BRAND_META_PIXEL_ID`.
 * @param advancedMatching Datos hasheados client-side (em, ph, fn, ln, ge, db, ct, st, zp, country, external_id).
 *   Meta los normaliza+hashea internamente; mandar en plain (Meta lo hashea con SHA-256 antes de salir del browser).
 */
export function loadMetaPixel(
  pixelId: string = BRAND_META_PIXEL_ID,
  advancedMatching?: Record<string, string>,
): void {
  if (typeof window === "undefined") return;
  const id = (pixelId || "").trim();
  if (!id) {
    if (import.meta.env.DEV) {
      console.warn("[MetaPixel] BRAND_META_PIXEL_ID vacío — pixel no cargado.");
    }
    return;
  }
  if (isPixelAlreadyInitialized()) return;

  injectFbqStub();

  if (typeof window.fbq !== "function") return;

  try {
    if (advancedMatching && Object.keys(advancedMatching).length > 0) {
      window.fbq("init", id, advancedMatching);
    } else {
      window.fbq("init", id);
    }
    window.fbq("track", "PageView");
    markPixelInitialized();
    if (import.meta.env.DEV) {
      console.log("[MetaPixel] Initialized:", id);
    }
  } catch (error) {
    console.error("[MetaPixel] Init error:", error);
  }
}

/** Lee la cookie `name` del document. */
function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Lee `_fbp` (browser id) y `_fbc` (click id) que setea fbevents.js cuando el
 * usuario llega con `fbclid=...` en la URL.  Si `fbclid` está en la URL y `_fbc`
 * no se ve aún, construimos el formato `fb.subdomain.timestamp.fbclid` que
 * Meta espera para CAPI (esto cubre la ventana entre landing y primer evento).
 */
export function getFbpFbcCookies(): { fbp: string | null; fbc: string | null } {
  if (typeof window === "undefined") {
    return { fbp: null, fbc: null };
  }
  const fbp = readCookie("_fbp");
  let fbc = readCookie("_fbc");
  if (!fbc) {
    try {
      const params = new URLSearchParams(window.location.search);
      const fbclid = params.get("fbclid");
      if (fbclid) {
        fbc = `fb.1.${Date.now()}.${fbclid}`;
      }
    } catch {
      /* noop */
    }
  }
  return { fbp, fbc };
}

/**
 * Genera un `event_id` corto, único por evento, para deduplicar con CAPI.
 * Formato: `<timestampMs>-<random>` (no PII, no necesita crypto.randomUUID
 * para no fallar en navegadores viejos / iframes inseguros).
 */
export function generateMetaEventId(): string {
  const rand = Math.random().toString(36).slice(2, 10);
  return `${Date.now().toString(36)}-${rand}`;
}

type FbqOptions = { eventID?: string };

function safeFbq(
  action: "track" | "trackCustom",
  eventName: string,
  params?: Record<string, unknown>,
  options?: FbqOptions,
): void {
  if (typeof window === "undefined") return;
  if (typeof window.fbq !== "function") {
    if (import.meta.env.DEV) {
      console.warn(`[MetaPixel] fbq no cargado — evento "${eventName}" descartado.`);
    }
    return;
  }
  try {
    if (options?.eventID) {
      window.fbq(action, eventName, params, options);
    } else {
      window.fbq(action, eventName, params);
    }
    if (import.meta.env.DEV) {
      console.log(
        `[MetaPixel] ${action === "track" ? "Track" : "Track Custom"}:`,
        eventName,
        params,
        options,
      );
    }
  } catch (error) {
    console.error(`[MetaPixel] Error tracking ${eventName}:`, error);
  }
}

/**
 * Standard event con soporte para dedup (`eventID`).
 * Eventos válidos: PageView, ViewContent, AddToCart, AddToWishlist,
 * InitiateCheckout, AddPaymentInfo, Purchase, Lead, CompleteRegistration,
 * Contact, Subscribe, Schedule, StartTrial, etc.
 *
 * Ver lista completa: https://developers.facebook.com/docs/meta-pixel/reference
 */
export function trackMetaEvent(
  eventName: string,
  parameters?: Record<string, unknown>,
  options?: FbqOptions,
): void {
  safeFbq("track", eventName, parameters, options);
}

/** Custom event (cualquier nombre — usar cuando el estándar no encaja). */
export function trackMetaCustomEvent(
  eventName: string,
  parameters?: Record<string, unknown>,
  options?: FbqOptions,
): void {
  safeFbq("trackCustom", eventName, parameters, options);
}

/**
 * Dispara `PageView` (re-init no necesario — `fbq('init')` ya lo emitió la
 * primera vez). Llamar en cada route change del SPA.
 */
export function trackPageViewMetaPixel(): void {
  safeFbq("track", "PageView");
}

// ─── Standard event helpers (alias compatibles con código existente) ────────

export function trackPurchase(
  value: number,
  currency: string,
  contentName: string,
  additionalParams?: Record<string, unknown>,
  options?: FbqOptions,
): void {
  trackMetaEvent(
    "Purchase",
    {
      value,
      currency,
      content_name: contentName,
      content_type: "product",
      ...additionalParams,
    },
    options,
  );
}

export function trackInitiateCheckout(
  value: number,
  currency: string,
  contentName: string,
  additionalParams?: Record<string, unknown>,
  options?: FbqOptions,
): void {
  trackMetaEvent(
    "InitiateCheckout",
    {
      value,
      currency,
      content_name: contentName,
      content_type: "product",
      ...additionalParams,
    },
    options,
  );
}

export function trackLead(
  contentName: string,
  additionalParams?: Record<string, unknown>,
  options?: FbqOptions,
): void {
  trackMetaEvent(
    "Lead",
    {
      content_name: contentName,
      content_category: "form_submission",
      ...additionalParams,
    },
    options,
  );
}

export function trackViewContent(
  contentName: string,
  contentType: string,
  additionalParams?: Record<string, unknown>,
  options?: FbqOptions,
): void {
  trackMetaEvent(
    "ViewContent",
    {
      content_name: contentName,
      content_type: contentType,
      ...additionalParams,
    },
    options,
  );
}

// ─── Backwards-compat aliases (no romper imports antiguos) ──────────────────

/** @deprecated Usar `trackMetaEvent`. */
export const trackFacebookEvent = trackMetaEvent;

/** @deprecated Usar `trackMetaCustomEvent`. */
export const trackCustomFacebookEvent = trackMetaCustomEvent;

/** @deprecated Usar `isMetaPixelLoaded`. */
export const isFacebookPixelLoaded = isMetaPixelLoaded;
